import { useMemo, forwardRef } from "react";
import { graphql, useMutation } from "react-relay";
import { useIntl, FormattedMessage, IntlShape } from "react-intl";
import { useNavigate } from "react-router-dom";
import * as v from "valibot";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Button } from "@/kit/ui/button";
import { PlusIcon, Trash01Icon } from "@/icons";
import { Input } from "@/kit/ui/input";
import { Textarea } from "@/kit/ui/textarea";
import { useValibotForm } from "@/utils/validation";
import { toast } from "@/utils/toast";
import { cn } from "@/utils/tailwind";
import { DragList, DragListItem, DragListHandle } from "@/components/DragList";
import { useDragListItemState } from "@/components/DragList/utils";

import ErrorPage from "./ErrorPage";

import { useCompetitionScoringCriteriaPageQuery } from "./loaders/CompetitionScoringCriteriaPage";
import { CompetitionScoringCriteriaPageMutation as CompetitionScoringCriteriaPageMutationType } from "./__generated__/CompetitionScoringCriteriaPageMutation.graphql";
import { relayErrorMessage } from "@/utils/relay";

const CompetitionScoringCriteriaPageMutation = graphql`
  mutation CompetitionScoringCriteriaPageMutation(
    $id: ID!
    $input: UpdateCompetitionInput!
  ) {
    updateCompetition(id: $id, input: $input) {
      node {
        id
        scoringCriteria {
          id
          title
          description
          weight
        }
      }
    }
  }
`;

const CRITERIA_KEY = "scoringCriteria";

const schemaFactory = (intl: IntlShape) =>
  v.object({
    [CRITERIA_KEY]: v.pipe(
      v.array(
        v.object({
          id: v.optional(v.string()),
          title: v.pipe(
            v.string(),
            v.minLength(
              1,
              intl.formatMessage({ defaultMessage: "Name is required" }),
            ),
          ),
          description: v.pipe(
            v.string(),
            v.transform((bio) => (bio?.trim() ? bio : undefined)),
          ),
          weight: v.pipe(
            v.string(),
            v.transform(parseFloat),
            v.transform((weight) => weight / 100),
          ),
        }),
      ),
      v.check(
        (criterion) =>
          new Set(criterion.map(({ title }) => title)).size ===
          criterion.length,
        intl.formatMessage({
          defaultMessage: "Names must be unique",
        }),
      ),
      v.check(
        (criterion) => {
          const sum = criterion
            .map(({ weight }) => weight)
            .reduce((a, b) => a + b, 0);
          return Math.abs(1 - sum) < 100 * Number.EPSILON;
        },
        intl.formatMessage({
          defaultMessage: "Percentages must sum to 100%",
        }),
      ),
    ),
  });

interface ScoringCriterionProps {
  name: `${typeof CRITERIA_KEY}.${number}`;
  title: string;
  onRemove: () => void;
}

const ScoringCriterion = forwardRef<HTMLDivElement, ScoringCriterionProps>(
  ({ name, title, onRemove }, ref) => {
    const form =
      useFormContext<v.InferInput<ReturnType<typeof schemaFactory>>>();
    const { dragOverEdge, isDragging, isLastMoved } = useDragListItemState();

    return (
      <div
        ref={ref}
        className={cn(
          isDragging && "opacity-50",
          dragOverEdge === "top" && "border-t-4 border-brand-200",
          dragOverEdge === "bottom" && "border-b-4 border-brand-200",
          isLastMoved && "animate-in fade-in-50 duration-500",
          "py-2 relative grid grid-cols-subgrid col-span-3 md:col-span-5",
        )}
      >
        <div>
          <FormItem>
            <FormLabel className="invisible">
              <FormattedMessage defaultMessage="Drag" />
            </FormLabel>
            <div>
              <DragListHandle
                previewOffset={{
                  x: "1em",
                  y: "0.25em",
                }}
                preview={
                  <div
                    className={cn(
                      !title && "text-grey-400",
                      "py-2 px-3 bg-brand-50 rounded-lg shadow-lg",
                    )}
                  >
                    {title || <FormattedMessage defaultMessage="No Name" />}
                  </div>
                }
              />
            </div>
          </FormItem>
        </div>
        <div className="flex flex-col space-y-4 md:space-y-0 md:grid md:grid-cols-subgrid md:col-span-3">
          <div>
            <FormField
              control={form.control}
              name={`${name}.title`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage defaultMessage="Name" />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <FormField
              control={form.control}
              name={`${name}.description`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage defaultMessage="Description" />
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={1} autoResize {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <FormField
              control={form.control}
              name={`${name}.weight`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <FormattedMessage defaultMessage="Weight" />
                  </FormLabel>
                  <FormControl>
                    <Input
                      endDecorator="%"
                      inputProps={{ className: "text-right" }}
                      type="number"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div>
          <FormItem>
            <FormLabel className="invisible">
              <FormattedMessage defaultMessage="Delete" />
            </FormLabel>
            <div>
              <Button variant="secondary" icon onClick={() => onRemove()}>
                <Trash01Icon />
              </Button>
            </div>
          </FormItem>
        </div>
      </div>
    );
  },
);

const ScoringCriteria = () => {
  const form = useFormContext<v.InferInput<ReturnType<typeof schemaFactory>>>();
  const { move, append, remove, fields } = useFieldArray({
    control: form.control,
    name: CRITERIA_KEY,
  });
  return (
    <>
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[auto_repeat(3,minmax(0,1fr))_auto] gap-x-2 gap-y-8 md:gap-y-0">
        <DragList move={move} length={fields.length}>
          {fields.map((field, index) => (
            <DragListItem key={field.id} index={index}>
              <ScoringCriterion
                title={field.title}
                name={`${CRITERIA_KEY}.${index}`}
                onRemove={() => {
                  remove(index);
                }}
              />
            </DragListItem>
          ))}
        </DragList>
      </div>
      <div className="flex justify-center">
        <Button
          variant="secondary"
          onClick={() => {
            append({
              title: "",
              description: "",
              weight: "0",
            });
          }}
        >
          <PlusIcon />
          <FormattedMessage defaultMessage="New Criterion" />
        </Button>
      </div>
    </>
  );
};

export default function CompetitionScoringCriteriaPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    query: { competitionBySlug: competition },
  } = useCompetitionScoringCriteriaPageQuery();
  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionScoringCriteriaPageMutationType>(
      CompetitionScoringCriteriaPageMutation,
    );
  const formSchema = useMemo(() => schemaFactory(intl), [intl]);
  const form = useValibotForm(formSchema, {
    mode: "onTouched",
    defaultValues: {
      [CRITERIA_KEY]:
        competition?.scoringCriteria.map(
          ({ id, title, description, weight }) => ({
            id,
            title: title || "",
            description: description || "",
            weight: (weight * 100 || 0).toString(),
          }),
        ) || [],
    },
  });

  if (!competition) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Competition not found",
        })}
      />
    );
  }

  if (!competition.viewerCanEdit) {
    return (
      <ErrorPage
        status={401}
        message={intl.formatMessage({
          defaultMessage: "Cannot edit competition",
        })}
      />
    );
  }

  const onSubmit = ({
    [CRITERIA_KEY]: scoringCriteria,
  }: v.InferOutput<typeof formSchema>) => {
    commitMutation({
      variables: {
        id: competition.id,
        input: {
          scoringCriteria,
        },
      },
      onCompleted: () => {
        toast.success(
          intl.formatMessage({
            defaultMessage: "Scoring criteria successfully updated",
          }),
        );
        navigate(`/competitions/${competition.slug}/approval`);
      },
      onError: (error) => {
        toast.error(relayErrorMessage(error));
      },
    });
  };

  return (
    <Form form={form} onSubmit={onSubmit}>
      <div className="space-y-8">
        <div className="space-y-2">
          <div className="flex justify-between">
            <div>
              <h1 className="text-xl font-semibold">
                <FormattedMessage defaultMessage="Edit Scoring Criteria" />
              </h1>
              <p className="text-sm text-grey-500">
                <FormattedMessage defaultMessage="Manage scoring criteria for judges" />
              </p>
            </div>
            <div>
              <Button type="submit" disabled={isMutationInFlight}>
                <FormattedMessage defaultMessage="Save" />
              </Button>
            </div>
          </div>
          {form.formState.errors.scoringCriteria?.root && (
            <div className="text-sm text-error-500 text-right">
              {form.formState.errors.scoringCriteria?.root.message}
            </div>
          )}
        </div>
        <ScoringCriteria />
      </div>
    </Form>
  );
}
